/**
 * Administração › Conectores: integrações por API no catálogo — Nexora Grid (frontend, US-2003).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * Cadastro estrutural do integrador. A configuração (equipamentos, sensores, mapeamento) é editada em JSON e validada no
 * servidor pelos mesmos modelos do conector. O token é só de escrita: a tela mostra "definido / não definido".
 * Ativar exige "Testar conexão" com a configuração atual; alterar um conector ativo o devolve a rascunho.
 * US-2004: o detalhe mostra a saúde (mesmo dado do selo da cidade no CCO).
 * v2.0 (US-4604, Sprint 46): painel de estado do guia (§4.4) — a lista vira cartões de estado (situação, último teste
 *   com a idade, token) com KPIs que filtram e filtros no endereço; o detalhe mostra cartões de coleta, teste e token com
 *   o botão junto do estado que ele resolve, e a saúde com forma e cor (ISA-101). Mesmas chamadas e as mesmas regras.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import {
  BarraFiltros, CabecalhoTela, Campo, EstadoAlarme, EstadoVazio, ErroCarregar, Esqueleto, FaixaKpis, GrupoCampos, RodapeFormulario, Segmentado,
  Selo, enderecoDeVolta, useAvisoAoSair, useLembrarEndereco, useParametro, type Estado,
} from "../componentes/Tela";
import { casaBusca, idadeRelativa, ordenar } from "../componentes/regrasTela";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import type { SaudeConector } from "../cco/dados";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { quando } from "./tipos";
import { marcaAtual } from "../marca";
import "./admin-b.css";

type Conector = { nome: string; empresa: string; tipo: "ih2o" | "rest"; configuracao: Record<string, unknown>; segredo_definido: boolean;
  situacao: "rascunho" | "ativo" | "pausado"; versao: number; criado_por: string; atualizado_em: string; origem_id: string;
  teste: { em: string | null; ok: boolean | null; mensagem: string | null; versao_atual: boolean }; pode_editar?: boolean };

const SITUACAO: Record<Conector["situacao"], [string, string]> = { rascunho: ["Rascunho", "situacao-pendente"],
  ativo: ["Ativo", "situacao-validado"], pausado: ["Pausado", "situacao-rejeitado"] };
const TOM_SITUACAO: Record<Conector["situacao"], "ok" | "atencao" | "suave"> = { ativo: "ok", rascunho: "atencao", pausado: "suave" };
const TIPO: Record<Conector["tipo"], string> = { ih2o: "iH2O (Raptor)", rest: "REST genérico" };
const MODELO: Record<Conector["tipo"], Record<string, unknown>> = {
  ih2o: { fonte: { intervalo_segundos: 300, janela_inicial_horas: 24 }, ih2o: { filial: "", municipio: "",
    equipamentos: { ID_NA_API: { unidade: "", tipo: "reservatorio", sensores: ["f"], medidas: { f: "nivel_m" } } },
    sensores: { f: { medida: "pressao_mca" } } } },
  rest: { fonte: { intervalo_segundos: 300 }, rest: { url: "https://", autenticacao: "bearer" },
    mapeamento: { caminho_lista: "", campo_tempo: "timestamp", campo_valor: "valor", medida_fixa: "pressao_mca",
      dimensoes: { device_id: "id" }, fixas: { filial: "", municipio: "", unidade: "" } } },
};
const ROTULO_KPI: Record<string, string> = { ativo: "Ativos", rascunho: "Rascunhos", pausado: "Pausados", falhou: "Teste falhou" };
const MIGALHAS = [{ texto: NOMES.administracao }, { texto: NOMES.conectores, para: "/admin/conectores" }];

/** Teste da conexão em forma e texto: ok, falhou ou nunca testado. */
function EstadoTeste({ c }: { c: Conector }) {
  if (!c.teste.em) return <EstadoAlarme estado="semtel" texto="nunca testado" />;
  return c.teste.ok ? <EstadoAlarme estado="normal" texto={`teste ok${c.teste.versao_atual ? "" : " (versão anterior)"}`} />
    : <EstadoAlarme estado="medio" texto="teste falhou" />;
}
const passaKpi = (c: Conector, kpi: string) => !kpi || (kpi === "falhou" ? c.teste.em !== null && c.teste.ok === false : c.situacao === kpi);

export function Conectores() {
  const { usuario } = useSessao();
  useLembrarEndereco();
  const [lista, setLista] = useState<Conector[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useParametro("busca");
  const [kpi, setKpi] = useParametro("kpi");
  const [tipo, setTipo] = useParametro("tipo");
  const [operadora, setOperadora] = useParametro("operadora");
  const carregar = useCallback(() => {
    setErro(null);
    api<Conector[]>("/v1/admin/conectores").then(setLista).catch((e) => setErro(String(e.message ?? e)));
  }, []);
  useEffect(carregar, [carregar]);
  const operadoras = useMemo(() => [...new Set((lista ?? []).map((c) => c.empresa))].sort(), [lista]);
  const visiveis = useMemo(() => ordenar((lista ?? []).filter((c) => passaKpi(c, kpi) && (!tipo || c.tipo === tipo)
    && (!operadora || c.empresa === operadora) && casaBusca(busca, c.nome, c.empresa, TIPO[c.tipo])), (c) => c.nome), [lista, kpi, tipo, operadora, busca]);
  if (!usuario || !pode.verEstrutura(usuario)) return <p className="nx-suave">Cadastro estrutural: só a equipe {marcaAtual().empresa}.</p>;
  const conta = (k: string) => (lista ?? []).filter((c) => passaKpi(c, k)).length;
  const pilulas = [
    ...(kpi ? [{ chave: "kpi", texto: ROTULO_KPI[kpi] ?? kpi }] : []), ...(tipo ? [{ chave: "tipo", texto: TIPO[tipo as Conector["tipo"]] ?? tipo }] : []),
    ...(operadora ? [{ chave: "operadora", texto: `Operadora ${operadora}` }] : []), ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (c: string) => ({ kpi: setKpi, tipo: setTipo, operadora: setOperadora, busca: setBusca } as Record<string, (v: string) => void>)[c]?.("");
  const limpar = () => { setKpi(""); setTipo(""); setOperadora(""); setBusca(""); };

  return (
    <section className="tl-pagina admin-b">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[{ texto: NOMES.administracao }, { texto: NOMES.conectores }]} titulo={NOMES.conectores}
        subtitulo="integrações por API"
        acoes={pode.editarEstrutura(usuario) ? <Link className="nx-botao nx-botao-principal" to="/admin/conectores/novo">Novo conector</Link> : undefined} />
      {erro ? <ErroCarregar mensagem={`Não foi possível carregar os conectores: ${erro}`} aoTentar={carregar} /> : !lista ? <Esqueleto linhas={6} /> : (
        <>
          <FaixaKpis ativo={kpi} aoEscolher={setKpi} itens={[
            { chave: "", rotulo: "Conectores", valor: lista.length, detalhe: `${operadoras.length} operadora(s)` },
            { chave: "ativo", rotulo: "Ativos", valor: conta("ativo"), detalhe: "coletando" },
            { chave: "rascunho", rotulo: "Rascunhos", valor: conta("rascunho"), detalhe: "testar e ativar" },
            { chave: "pausado", rotulo: "Pausados", valor: conta("pausado"), detalhe: "sem coleta" },
            { chave: "falhou", rotulo: "Teste falhou", valor: conta("falhou"), detalhe: "último teste com erro", tom: conta("falhou") ? "alarme" : undefined },
          ]} />
          <BarraFiltros busca={busca} aoBuscar={setBusca} placeholder="Buscar por nome, operadora ou tipo" pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
            {operadoras.length > 1 && (
              <>
                <label htmlFor="f-operadora-con">Operadora</label>
                <select id="f-operadora-con" value={operadora} onChange={(e) => setOperadora(e.target.value)}>
                  <option value="">Todas</option>{operadoras.map((o) => <option key={o}>{o}</option>)}</select>
              </>
            )}
            <Segmentado rotulo="Tipo de conector" valor={tipo} aoMudar={setTipo}
              opcoes={[{ valor: "", texto: "Todos" }, { valor: "ih2o", texto: "iH2O" }, { valor: "rest", texto: "REST" }]} />
          </BarraFiltros>
          {!visiveis.length ? (
            <div className="tl-bloco"><EstadoVazio titulo={lista.length ? "Nenhum conector com esses filtros" : "Nenhum conector cadastrado"}
              texto={lista.length ? "Tire um filtro para ver os outros." : "Cadastre a primeira integração por API."}
              acao={pilulas.length ? <button type="button" className="nx-botao" onClick={limpar}>Limpar filtros</button>
                : pode.editarEstrutura(usuario) ? <Link className="nx-botao" to="/admin/conectores/novo">Cadastrar conector</Link> : undefined} /></div>
          ) : (
            <ul className="cartoes-estado" aria-label="Conectores" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {visiveis.map((c) => {
                const falhou = c.teste.em !== null && c.teste.ok === false;
                const classe = falhou ? "ruim" : c.situacao === "ativo" ? "ok" : c.situacao === "rascunho" ? "atencao" : "semcom";
                return (
                  <li key={c.nome} className={`cartao-estado ${classe}`}>
                    <header><Link className="nx-valor" to={`/admin/conectores/${c.nome}`}><b>{c.nome}</b></Link><span className="espaco" />
                      <Selo tom={TOM_SITUACAO[c.situacao]}>{SITUACAO[c.situacao][0]}</Selo></header>
                    <small>{c.empresa} · {TIPO[c.tipo]}</small>
                    <p><EstadoTeste c={c} />{c.teste.em && <span className="idade" title={quando(c.teste.em)}> · {idadeRelativa(c.teste.em)}</span>}</p>
                    <small>Token: {c.segredo_definido ? "definido" : "não definido"} · versão {c.versao} · alterado {idadeRelativa(c.atualizado_em)}</small>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function CampoJson({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }) {
  let erro: string | null = null;
  try { JSON.parse(valor); } catch (e) { erro = (e as Error).message; }
  return (
    <Campo id="con-config" rotulo="Configuração (JSON)" largo erro={erro ? `JSON inválido: ${erro}` : null}
      ajuda="validada no servidor pelos mesmos modelos do conector">
      <textarea id="con-config" className="json-conector" rows={18} spellCheck={false} value={valor} onChange={(e) => aoMudar(e.target.value)} />
    </Campo>
  );
}
const jsonValido = (v: string) => { try { JSON.parse(v); return true; } catch { return false; } };

export function ConectorNovo() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const formulario = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({ nome: "", empresa: "", tipo: "ih2o" as Conector["tipo"], segredo: "", usuario: "" });
  const [config, setConfig] = useState(JSON.stringify(MODELO.ih2o, null, 2));
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const sujo = !!(form.nome || form.empresa || form.segredo || form.usuario) || config !== JSON.stringify(MODELO[form.tipo], null, 2);
  useAvisoAoSair(sujo && !enviando);
  if (!usuario || !pode.editarEstrutura(usuario)) return <p className="nx-suave">Cadastro estrutural: só a equipe {marcaAtual().empresa}.</p>;
  const nomeValido = /^[a-z0-9][a-z0-9_-]{0,39}$/.test(form.nome);
  const valido = nomeValido && !!form.empresa && jsonValido(config);
  const criar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const c = await api<Conector>("/v1/admin/conectores", { corpo: { ...form, segredo: form.segredo || null, usuario: form.usuario || null,
        configuracao: JSON.parse(config) } });
      navegar(`/admin/conectores/${c.nome}`);
    } catch (x) { setErro(x instanceof ErroApi ? x.message : String((x as Error).message ?? x)); setEnviando(false); }
  };
  return (
    <section className="tl-pagina admin-b">
      <CabecalhoTela voltar="/admin/conectores" migalhas={[...MIGALHAS, { texto: "Novo conector" }]} titulo="Novo conector"
        subtitulo="nasce em rascunho: teste a conexão antes de ativar" />
      <form ref={formulario} className="tl-bloco" onSubmit={(e) => void criar(e)}>
        <div className="form-b">
          <GrupoCampos titulo="Identificação">
            <Campo id="con-nome" rotulo="Nome da fonte" erro={form.nome && !nomeValido ? "Use minúsculas, números, hífen ou sublinhado (até 40)." : null}
              ajuda="vira a origem no hub (ex.: jardim-ih2o)">
              <input id="con-nome" required pattern="[a-z0-9][a-z0-9_\-]{0,39}" value={form.nome} placeholder="ex.: jardim-ih2o"
                onChange={(e) => setForm({ ...form, nome: e.target.value.trim().toLowerCase() })} />
            </Campo>
            <Campo id="con-empresa" rotulo="Operadora">
              <input id="con-empresa" required value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value.trim() })} />
            </Campo>
            <Campo id="con-tipo" rotulo="Tipo">
              <select id="con-tipo" value={form.tipo} onChange={(e) => { const t = e.target.value as Conector["tipo"]; setForm({ ...form, tipo: t }); setConfig(JSON.stringify(MODELO[t], null, 2)); }}>
                <option value="ih2o">iH2O (Raptor)</option><option value="rest">REST genérico</option>
              </select>
            </Campo>
          </GrupoCampos>
          <GrupoCampos titulo="Credencial da fonte">
            <Campo id="con-segredo" rotulo="Token / senha da fonte" ajuda="só de escrita: depois de salvo, a tela mostra apenas se está definido">
              <input id="con-segredo" type="password" autoComplete="off" value={form.segredo} onChange={(e) => setForm({ ...form, segredo: e.target.value })} />
            </Campo>
            {form.tipo === "rest" && (
              <Campo id="con-usuario" rotulo="Usuário (só autenticação basic)">
                <input id="con-usuario" autoComplete="off" value={form.usuario} onChange={(e) => setForm({ ...form, usuario: e.target.value })} />
              </Campo>
            )}
          </GrupoCampos>
          <GrupoCampos titulo="Configuração">
            <CampoJson valor={config} aoMudar={setConfig} />
          </GrupoCampos>
          {erro && <p className="erro" role="alert">{erro}</p>}
          <RodapeFormulario sujo={sujo} valido={valido} salvando={enviando} rotuloSalvar="Cadastrar conector"
            aoCancelar={() => navegar(enderecoDeVolta("/admin/conectores"))} aoSalvar={() => formulario.current?.requestSubmit()} />
        </div>
      </form>
    </section>
  );
}

const ESTADO: Record<SaudeConector["estado"], [Estado, string]> = { ok: ["normal", "Coletando"], parcial: ["medio", "Parcial"],
  atrasado: ["semcom", "Atrasado"], falhando: ["alto", "Falhando"], sem_ciclo: ["semtel", "Sem ciclo ainda"], pausado: ["normal", "Pausado"] };

/** US-2004: saúde da integração (último ciclo, pontos, falhas seguidas, equipamentos sem dado, alarme). */
function Saude({ nome, empresa }: { nome: string; empresa: string }) {
  const [s, setS] = useState<SaudeConector | null | undefined>(undefined);
  useEffect(() => {
    const carregar = () => api<SaudeConector[]>(`/v1/conectores/saude?empresa=${empresa}`).then((l) => setS(l.find((x) => x.nome === nome) ?? null)).catch(() => setS(null));
    void carregar();
    const relogio = window.setInterval(() => void carregar(), 60000);
    return () => window.clearInterval(relogio);
  }, [nome, empresa]);
  if (s === undefined) return null;
  if (s === null) return <p className="tl-texto">Saúde: o conector ainda não está ativo.</p>;
  const [estado, rotulo] = ESTADO[s.estado];
  return (
    <section className="tl-bloco" aria-label="Saúde do conector">
      <div className="tl-bloco-cab"><h3>Saúde</h3><EstadoAlarme estado={estado} texto={rotulo} /><span className="espaco" />
        {s.ciclo && <span className="nx-suave" title={quando(s.ciclo.em)}>último ciclo {idadeRelativa(s.ciclo.em)}</span>}</div>
      <div className="tl-bloco-corpo">
        <dl className="tl-kv">
          <dt>Último ciclo</dt><dd>{s.ciclo ? <>{quando(s.ciclo.em)} · {s.ciclo.coletados} coletado(s), {s.ciclo.inseridos} inserido(s), {s.ciclo.descartados} descartado(s)</> : "nenhum"}</dd>
          <dt>Último ciclo ok</dt><dd>{s.ciclo?.ok_em ? <>{quando(s.ciclo.ok_em)} <small className="nx-suave">({idadeRelativa(s.ciclo.ok_em)})</small></> : "—"}</dd>
          <dt>Falhas seguidas</dt><dd>{s.ciclo?.falhas_seguidas ?? 0}{s.ciclo?.ultimo_erro ? ` · ${s.ciclo.ultimo_erro}` : ""}</dd>
          <dt>Equipamentos sem dado</dt><dd>{s.sem_dado.length ? s.sem_dado.map((d) => `${d.device_id} (${d.ultimo ? `último ${quando(d.ultimo)}` : "sem leitura em 7 dias"})`).join(" · ")
            : s.equipamentos ? `nenhum (de ${s.equipamentos})` : "—"}</dd>
          <dt>Alarme de falha (24 h)</dt><dd>{s.alarme ? `${quando(s.alarme.timestamp)} · ${s.alarme.descricao}` : "nenhum"}</dd>
        </dl>
      </div>
    </section>
  );
}

export function ConectorDetalhe() {
  const { nome } = useParams();
  const { usuario } = useSessao();
  const formulario = useRef<HTMLFormElement>(null);
  const [c, setC] = useState<Conector | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [config, setConfig] = useState<string | null>(null);
  const [segredo, setSegredo] = useState("");
  const [testando, setTestando] = useState(false);
  const carregar = useCallback(() => {
    api<Conector>(`/v1/admin/conectores/${nome}`).then((x) => { setC(x); setConfig(null); setErro(null); })
      .catch((x) => setErro(x instanceof ErroApi && x.status === 404 ? "Conector não encontrado." : String(x.message ?? x)));
  }, [nome]);
  useEffect(carregar, [carregar, usuario]);
  useAvisoAoSair(config !== null || !!segredo);
  if (!usuario) return null;
  if (erro) return (
    <section className="tl-pagina admin-b">
      <CabecalhoTela voltar="/admin/conectores" migalhas={[...MIGALHAS, { texto: nome ?? "" }]} titulo={<span className="tl-mono">{nome}</span>} />
      <ErroCarregar mensagem={erro} aoTentar={carregar} />
    </section>
  );
  if (!c || c.nome !== nome) return <section className="tl-pagina admin-b"><Esqueleto linhas={6} /></section>;
  if (!pode.verEmpresa(usuario, c.empresa)) return <p className="nx-suave">Conector não encontrado.</p>;
  const edita = c.pode_editar && pode.editarEstrutura(usuario);
  const base = `/v1/admin/conectores/${c.nome}`;
  const executar = async (acao: () => Promise<unknown>, ok: string) => {
    try { await acao(); setAviso(ok); setSegredo(""); carregar(); }
    catch (x) {
      if (x instanceof ErroApi && x.status === 409 && /versão/.test(x.message)) { setAviso("Outra pessoa alterou este conector antes. Os dados foram recarregados."); carregar(); }
      else setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : String((x as Error).message ?? x));
    }
  };
  const testar = async () => {
    setTestando(true);
    try { const r = await api<{ ok: boolean; mensagem: string }>(`${base}/testar`, { corpo: {} }); setAviso(r.ok ? `Conexão ok: ${r.mensagem}` : `Falhou: ${r.mensagem}`); carregar(); }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
    finally { setTestando(false); }
  };
  const [rotulo, classe] = SITUACAO[c.situacao];
  const liberadoAtivar = !!c.teste.ok && c.teste.versao_atual;
  const falhou = c.teste.em !== null && c.teste.ok === false;
  const valorConfig = config ?? JSON.stringify(c.configuracao, null, 2);
  return (
    <section className="tl-pagina admin-b">
      <CabecalhoTela voltar="/admin/conectores" migalhas={[...MIGALHAS, { texto: c.nome }]} titulo={<span className="tl-mono">{c.nome}</span>}
        subtitulo={`${c.empresa} · ${TIPO[c.tipo]}`}
        selos={<span className="linha-titulo selos-cab"><span className={`nx-selo ${classe}`}>{rotulo}</span></span>} />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      <div className="cartoes-estado">
        <article className={`cartao-estado ${c.situacao === "ativo" ? "ok" : c.situacao === "rascunho" ? "atencao" : "semcom"}`} aria-label="Coleta">
          <header><small>Coleta</small><span className="espaco" /><Selo tom={TOM_SITUACAO[c.situacao]}>{rotulo}</Selo></header>
          <p>{c.situacao === "ativo" ? "O serviço coleta a fonte no intervalo configurado." : c.situacao === "rascunho"
            ? "Ainda não coleta: teste a conexão com a configuração atual e ative." : "Coleta parada. Ative de novo quando a fonte voltar."}</p>
          {edita && (
            <div className="acoes">
              {c.situacao !== "ativo" && <button type="button" className="nx-botao nx-botao-principal" disabled={!liberadoAtivar} title={liberadoAtivar ? "" : "teste com a configuração atual antes"}
                onClick={() => void executar(() => api(`${base}/ativar`, { corpo: {} }), "Conector ativo: o serviço passa a coletar em até 1 minuto.")}>Ativar</button>}
              {c.situacao === "ativo" && <button type="button" className="nx-botao" onClick={() => void executar(() => api(`${base}/pausar`, { corpo: {} }), "Conector pausado.")}>Pausar</button>}
              {c.situacao !== "ativo" && !liberadoAtivar && <small>teste com a configuração atual antes</small>}
            </div>
          )}
        </article>
        <article className={`cartao-estado ${falhou ? "ruim" : c.teste.em ? "ok" : "atencao"}`} aria-label="Último teste">
          <header><small>Último teste</small><span className="espaco" /><EstadoTeste c={c} /></header>
          <p>{c.teste.em ? <>{c.teste.ok ? "ok" : "falhou"} · {quando(c.teste.em)} — {c.teste.mensagem}
            {!c.teste.versao_atual && <small className="nx-suave"> (de uma versão anterior)</small>}</> : "nunca testado"}</p>
          {c.teste.em && <span className="idade">{idadeRelativa(c.teste.em)}</span>}
          {edita && <div className="acoes"><button type="button" className="nx-botao" disabled={testando} onClick={() => void testar()}>{testando ? "Testando…" : "Testar conexão"}</button></div>}
        </article>
        <article className={`cartao-estado ${c.segredo_definido ? "ok" : "atencao"}`} aria-label="Token">
          <header><small>Token</small></header>
          <p>{c.segredo_definido ? "definido (não é exibido)" : "não definido"}</p>
          <small>Origem no hub <span className="tl-mono">{c.origem_id}</span></small>
          <small>Versão {c.versao} · atualizado {quando(c.atualizado_em)} ({idadeRelativa(c.atualizado_em)})</small>
        </article>
      </div>
      <Saude nome={c.nome} empresa={c.empresa} />
      {edita ? (
        <form ref={formulario} className="tl-bloco" aria-label="Alterar conector" onSubmit={(e) => {
          e.preventDefault();
          void executar(() => api(base, { metodo: "PUT", corpo: { versao: c.versao, ...(config !== null ? { configuracao: JSON.parse(config) } : {}),
            ...(segredo ? { segredo } : {}) } }), c.situacao === "ativo" ? "Salvo. O conector voltou a rascunho: teste e ative de novo." : "Salvo.");
        }}>
          <div className="form-b">
            <GrupoCampos titulo="Alterar conector">
              <Campo id="con-segredo" rotulo="Novo token / senha (deixe vazio para manter)" largo>
                <input id="con-segredo" type="password" autoComplete="off" value={segredo} onChange={(e) => setSegredo(e.target.value)} />
              </Campo>
              <CampoJson valor={valorConfig} aoMudar={setConfig} />
              {c.situacao === "ativo" && <p className="tl-texto tl-campo largo">Salvar uma alteração pausa a coleta até um novo teste.</p>}
            </GrupoCampos>
            <RodapeFormulario sujo={config !== null || !!segredo} valido={jsonValido(valorConfig)} rotuloSalvar="Salvar alterações"
              aoCancelar={config !== null || segredo ? () => { setConfig(null); setSegredo(""); } : undefined}
              aoSalvar={() => formulario.current?.requestSubmit()} />
          </div>
        </form>
      ) : (
        <section className="tl-bloco" aria-label="Configuração">
          <div className="tl-bloco-cab"><h3>Configuração</h3></div>
          <pre className="json-conector">{JSON.stringify(c.configuracao, null, 2)}</pre>
        </section>
      )}
    </section>
  );
}
